import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Box, 
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Avatar,
  Divider,
  IconButton,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useDataProvider } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { BRAND_COLORS } from './constants/colors';
import { tenantService } from './services/tenantService';
import { useTenant } from './contexts/TenantContext';

const StatCard = ({ title, count, icon, color, onClick, subtitle }: any) => {
  return (
    <Card
    sx={{
      height: '100%',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      cursor: 'pointer',
      '&:hover': {
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
        transform: 'translateY(-2px)',
      },
      transition: 'all 0.2s ease',
    }}
    onClick={onClick}
  >
    <CardContent sx={{ padding: '24px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '8px',
            backgroundColor: `${color}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(icon, { sx: { color, fontSize: 24 } })}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#2D3748' }}>
            {count}
          </Typography>
          <Typography color="textSecondary" variant="body2">
            {title}
          </Typography>
          {subtitle && (
            <Typography color="textSecondary" variant="caption">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
  );
};

const DashboardWidget = ({ title, icon, color, children, actionText, onAction }: any) => {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: `${color}20`, color: color, width: 32, height: 32 }}>
              {icon}
            </Avatar>
            <Typography variant="h6" color="primary">
              {title}
            </Typography>
          </Box>
          {onAction && (
            <IconButton size="small" onClick={onAction} color="primary">
              <ArrowForwardIcon />
            </IconButton>
          )}
        </Box>
        {children}
      </CardContent>
      {actionText && onAction && (
        <Box sx={{ mt: 'auto', p: 2, pt: 0 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onAction}
            endIcon={<ArrowForwardIcon />}
          >
            {actionText}
          </Button>
        </Box>
      )}
    </Card>
  );
};

const RecentReminders = () => {
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReminders = async () => {
      try {
        const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
        const accessToken = localStorage.getItem('accessToken');
        const tenantCode = tenantService.getCurrentTenantCode();

        // Get overdue and pending reminders
        const response = await fetch(`${baseUrl}/reminders?status=overdue,pending&limit=5`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-tenant-code': tenantCode,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setReminders(data.slice(0, 5)); // Show only first 5
        }
      } catch (error) {
        console.error('Error fetching reminders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReminders();
  }, [currentTenant?.code]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'overdue': return 'error';
      case 'pending': return 'warning';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'overdue': return <WarningIcon sx={{ fontSize: 16 }} />;
      case 'pending': return <ScheduleIcon sx={{ fontSize: 16 }} />;
      case 'completed': return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      default: return <ScheduleIcon sx={{ fontSize: 16 }} />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return `${Math.abs(diffDays)} days overdue`;
    } else if (diffDays === 0) {
      return 'Due today';
    } else if (diffDays === 1) {
      return 'Due tomorrow';
    } else {
      return `Due in ${diffDays} days`;
    }
  };

  if (loading) {
    return <Typography>Loading reminders...</Typography>;
  }

  if (reminders.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No pending reminders
        </Typography>
      </Box>
    );
  }

  return (
    <List sx={{ p: 0 }}>
      {reminders.map((reminder: any, index) => (
        <React.Fragment key={reminder.id}>
          <ListItem sx={{ px: 0, py: 1 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              {getStatusIcon(reminder.status)}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {reminder.reminderType?.name || 'Reminder'}
                  </Typography>
                  <Chip
                    label={reminder.status?.toUpperCase()}
                    size="small"
                    color={getStatusColor(reminder.status)}
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </Box>
              }
              secondary={
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {reminder.equipment?.name || `Vehicle ${reminder.vehicleId}`}
                  </Typography>
                  <Typography variant="caption" display="block" color={
                    reminder.status === 'overdue' ? 'error.main' : 'text.secondary'
                  }>
                    {formatDate(reminder.reminderDate)}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          {index < reminders.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </List>
  );
};

const RecentNotifications = () => {
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
        const accessToken = localStorage.getItem('accessToken');
        const tenantCode = tenantService.getCurrentTenantCode();

        const response = await fetch(`${baseUrl}/notifications?limit=5&isRead=false`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-tenant-code': tenantCode,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setNotifications(data.data?.slice(0, 5) || []);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [currentTenant?.code]);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else {
      return `${diffDays}d ago`;
    }
  };

  if (loading) {
    return <Typography>Loading notifications...</Typography>;
  }

  if (notifications.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <NotificationsIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No new notifications
        </Typography>
      </Box>
    );
  }

  return (
    <List sx={{ p: 0 }}>
      {notifications.map((notification: any, index) => (
        <React.Fragment key={notification.id}>
          <ListItem sx={{ px: 0, py: 1 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <NotificationsActiveIcon 
                sx={{ 
                  fontSize: 16, 
                  color: notification.isSystem ? 'info.main' : 'warning.main' 
                }} 
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {notification.subject}
                </Typography>
              }
              secondary={
                <Box>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {notification.message}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    {formatTime(notification.createdAt)}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
          {index < notifications.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </List>
  );
};

export const Dashboard = () => {
  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const [usersCount, setUsersCount] = useState(0);
  const [driversCount, setDriversCount] = useState(0);
  const [vehiclesCount, setVehiclesCount] = useState(0);
  const [remindersStats, setRemindersStats] = useState({ total: 0, overdue: 0, pending: 0 });
  const [notificationsStats, setNotificationsStats] = useState({ total: 0, unread: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        setLoading(true);
        
        // Fetch total users count
        const usersResponse = await dataProvider.getList('users', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'id', order: 'ASC' },
          filter: {},
        });

        // Fetch drivers count (users with driver profiles)
        const driversResponse = await dataProvider.getList('users', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'id', order: 'ASC' },
          filter: { hasDriver: true },
        });

        // Fetch vehicles count
        const vehiclesResponse = await dataProvider.getList('equipment', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'id', order: 'ASC' },
          filter: {},
        });

        setUsersCount(usersResponse.total || 0);
        setDriversCount(driversResponse.total || 0);
        setVehiclesCount(vehiclesResponse.total || 0);

        // Fetch reminders and notifications stats
        const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
        const accessToken = localStorage.getItem('accessToken');
        const tenantCode = tenantService.getCurrentTenantCode();

        if (accessToken && tenantCode) {
          // Fetch reminders stats
          const remindersResponse = await fetch(`${baseUrl}/reminders`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'x-tenant-code': tenantCode,
            },
          });

          if (remindersResponse.ok) {
            const remindersData = await remindersResponse.json();
            const total = remindersData.length;
            const overdue = remindersData.filter((r: any) => r.status === 'overdue').length;
            const pending = remindersData.filter((r: any) => r.status === 'pending').length;
            setRemindersStats({ total, overdue, pending });
          }

          // Fetch notifications stats
          const notificationsResponse = await fetch(`${baseUrl}/notifications`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'x-tenant-code': tenantCode,
            },
          });

          if (notificationsResponse.ok) {
            const notificationsData = await notificationsResponse.json();
            const total = notificationsData.total || 0;
            const unread = notificationsData.data?.filter((n: any) => !n.isRead).length || 0;
            setNotificationsStats({ total, unread });
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard counts:', error);
        setUsersCount(0);
        setDriversCount(0);
        setVehiclesCount(0);
        setRemindersStats({ total: 0, overdue: 0, pending: 0 });
        setNotificationsStats({ total: 0, unread: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();
  }, [dataProvider, currentTenant?.code]);

  return (
    <Box sx={{ padding: '16px 24px' }}>
      <Typography 
        variant="h4" 
        gutterBottom 
        sx={{ 
          fontWeight: 600, 
          color: '#1976d2',
          marginBottom: '16px' 
        }}
      >
        Dashboard
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Users"
            count={loading ? "..." : usersCount}
            icon={<PeopleIcon />}
            color={BRAND_COLORS.PRIMARY}
            onClick={() => navigate('/users')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Drivers"
            count={loading ? "..." : driversCount}
            icon={<LocalShippingIcon />}
            color="#4caf50"
            onClick={() => navigate('/users?filter=%7B%22hasDriver%22%3A%22true%22%7D')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Vehicles"
            count={loading ? "..." : vehiclesCount}
            icon={<LocalShippingIcon />}
            color="#ff9800"
            onClick={() => navigate('/equipment')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Reminders"
            count={loading ? '...' : remindersStats.total}
            subtitle={
              !loading && remindersStats.overdue > 0
                ? `${remindersStats.overdue} overdue`
                : !loading && remindersStats.pending > 0
                ? `${remindersStats.pending} pending`
                : 'All caught up'
            }
            icon={<NotificationsActiveIcon />}
            color={remindersStats.overdue > 0 ? '#f44336' : '#ff9800'}
            onClick={() => navigate('/reminders')}
          />
        </Grid>

        {/* Temporarily hidden - notifications functionality needs fixes
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Notifications"
            count={loading ? '...' : notificationsStats.total}
            subtitle={
              !loading && notificationsStats.unread > 0
                ? `${notificationsStats.unread} unread`
                : 'All read'
            }
            icon={<NotificationsIcon />}
            color={notificationsStats.unread > 0 ? '#2196f3' : '#4caf50'}
            onClick={() => navigate('/notifications')}
          />
        </Grid>
        */}
      </Grid>

      {/* Dashboard Widgets */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <DashboardWidget
            title="Recent Reminders"
            icon={<NotificationsActiveIcon />}
            color={remindersStats.overdue > 0 ? '#f44336' : '#ff9800'}
            actionText="View All Reminders"
            onAction={() => navigate('/reminders')}
          >
            <RecentReminders />
          </DashboardWidget>
        </Grid>

        {/* Temporarily hidden - notifications functionality needs fixes
        <Grid item xs={12} md={6}>
          <DashboardWidget
            title="Recent Notifications"
            icon={<NotificationsIcon />}
            color="#2196f3"
            actionText="View All Notifications"
            onAction={() => navigate('/notifications')}
          >
            <RecentNotifications />
          </DashboardWidget>
        </Grid>
        */}
      </Grid>
    </Box>
  );
};